import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { useI18n } from '../i18n';
import { renderTurnstile, type VisibleWidget } from '../turnstile';

export interface TurnstileHandle {
  reset(): void;
}

/** Cloudflare's "Verify you are human" box. Reports each token (or null) to onToken. */
export function TurnstileWidget({ siteKey, onToken, ref }: { siteKey: string; onToken: (token: string | null) => void; ref?: Ref<TurnstileHandle> }) {
  const { t, locale } = useI18n();
  const el = useRef<HTMLDivElement>(null);
  const widget = useRef<VisibleWidget | null>(null);
  const tokenCb = useRef(onToken);
  tokenCb.current = onToken;
  const [failed, setFailed] = useState(false);

  useImperativeHandle(ref, () => ({
    reset: () => {
      tokenCb.current(null);
      widget.current?.reset();
    },
  }));

  useEffect(() => {
    let cancelled = false;
    if (!el.current) return;
    renderTurnstile(el.current, siteKey, locale, (token) => tokenCb.current(token), () => cancelled).then(
      (w) => {
        widget.current = w;
      },
      () => !cancelled && setFailed(true),
    );
    return () => {
      cancelled = true;
      widget.current?.remove();
      widget.current = null;
    };
  }, [siteKey, locale]);

  return (
    <div className="turnstile-box">
      <div ref={el} />
      {failed && <p className="small muted">{t('captcha.load_failed')}</p>}
    </div>
  );
}
